/*
  The KPI tile, which existed nine times under two names and two visual treatments.

  MEASURED BEFORE THIS EXISTED: `Tile` defined in `PerimeterPanel`, `TransfersPanel`, `TsaPanel`,
  `RemainCoPanel`, `StrandedPane` and `TsaEconomics`; `Stat` in `DataPanel`, `LevelPane` and
  `DedicationPane`. Four of those render `.pp-stat` — a filled well, 17px value, weight 700; five
  render `.stat` — no background, 18px, weight 600. Same idea, same data, two appearances.

  Nine was an undercount. `PerspectivePane`, `SummaryPane` and `Diagnostics` write the same three
  divs INLINE, twenty more times, without giving them a name at all — which is why the drift was
  never obvious to anybody reading one file.

  The consequence was not cosmetic. Moving from Perimeter to Stranded, a reader watched the numbers
  change shape, which reads as two tools stitched together rather than one product with two screens.

  ONE TREATMENT, AND `.pp-stat` WINS: it is the filled one, it is on the four phase panels a user
  spends most of their time in, and a tile that is visibly a tile survives sitting next to a table
  without needing a border of its own. The flat-variant surfaces gain a background when they adopt
  this. That is the deliberate half of the change, and the only visible one.

  TWO TESTID PLACEMENTS, KEPT, which looks like a wart and is the opposite. The `.pp-stat` copies put
  `data-testid` on the CONTAINER; the `.stat` copies put it on the VALUE. So `text(byId(…))` returns
  "Residual" + the number + the caption in one case and just the number in the other, and unifying
  the placement would silently change what a dozen existing assertions read — without renaming a
  single id, which is how it would have got past review. Each adopter passes the prop it already had
  and nothing moves. The inconsistency is absorbed here rather than migrated in forty files.
*/

export function StatTile({
  label,
  value,
  sub,
  danger,
  testId,
  valueTestId,
  attrs,
  children,
}: {
  label: string;
  value: string;
  /**
   * The caption under the number.
   *
   * A node rather than a string, because several of the inline copies compose theirs — "3
   * rightsized · 2 redeployed" is two formatted numbers and a separator, not a template somebody
   * chose to write awkwardly. Narrowing it to `string` would have forced twenty call sites to build
   * their captions differently from how they read.
   */
  sub?: React.ReactNode;
  /** For a figure that is bad news rather than merely large. Always paired with words. */
  danger?: boolean;
  /** On the tile, as the `.pp-stat` copies do. */
  testId?: string;
  /** On the number, as the `.stat` copies do. */
  valueTestId?: string;
  /**
   * Extra `data-*` on the tile.
   *
   * Not decoration: `data-hc`, `data-n` and `data-d1` are how the end-to-end suite reads a figure
   * back as a NUMBER rather than re-parsing "€1.2m" out of the rendered text. Three call sites carry
   * them, and dropping them in the name of a tidy prop list would have broken two checks.
   *
   * `undefined` is allowed through because React omits an attribute set to it, which is the
   * behaviour the inline copies had: the flows tile carries `data-d1` only for the one flow that
   * has a Day 1 share. Narrowing this to `string | number` would have forced the caller to build
   * two different objects to say the same thing.
   */
  attrs?: Record<string, string | number | undefined>;
  /** Anything after the caption. One pane adds a fourth line to some of its tiles. */
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="pp-stat" data-testid={testId} {...attrs}>
      <div className="lbl">{label}</div>
      <div className={danger ? 'val bad' : 'val'} data-testid={valueTestId}>
        {value}
      </div>
      {/*
        Rendered whenever the caller passed one, INCLUDING an empty string.

        Six of the nine copies type `sub` as required and several pass `''` for a tile that has no
        caption — and the empty div is load-bearing: `.pp-stat` is a grid cell, and dropping the
        third row makes that one tile shorter than the ones beside it. Only an omitted prop means
        "this tile has two lines".
      */}
      {sub !== undefined && <div className="sub">{sub}</div>}
      {children}
    </div>
  );
}
